/**
 * Where a rolled-back organization is in the drain (SD-10965).
 *
 * ⚠️ A DIFFERENT ENUM FROM {@link TreasuryConversionStage}, AND IT HAS TO BE. That one answers
 * "what is this organization's conversion doing" and deliberately collapses every ENDED state into
 * `NONE` — a rolled-back organization reads `NONE` there, because it has no conversion experience
 * left to show.
 *
 * This one exists precisely for the weeks AFTER that `NONE`, when the organization still has money
 * in a Treasury account it no longer converts into. Folding these values into the other enum would
 * force a rolled-back organization to read as mid-conversion on every screen keyed off it.
 *
 * Received, never computed. Core derives it from the conversion's lifecycle timestamps and the
 * financial account's own status; there is no stored stage column and the UI must not invent one.
 */
export enum TreasuryDrainStage {
    /**
     * Not in a rollback at all — converted and running, never converted, or an organization
     * outside the conversion entirely.
     *
     * ⚠️ THIS IS WHAT THE OVERWHELMING MAJORITY OF ORGANIZATIONS READ, and the drain panel renders
     * NOTHING for it. The endpoint answers 200 with this rather than 404 so that an organization
     * outside the conversion behaves exactly as it did before any of this existed.
     */
    NONE = 'none',

    /**
     * Frozen, teardown still running.
     *
     * The organization already reads as un-converted, but the restore may be many runs away. The
     * panel shows ONE "finalizing" line and no figures, because the figures are not yet meaningful.
     */
    FINALIZING = 'finalizing',

    /** Teardown done, money still moving to the chosen bank. */
    DRAINING = 'draining',

    /**
     * Drained, waiting out the quiet period before the account closes.
     *
     * ⚠️ AN ORGANIZATION CAN LEAVE THIS STAGE BACKWARDS. A transaction arriving here returns it to
     * `DRAINING`, so nothing in the UI may treat this as terminal or cache it as final.
     */
    AWAITING_CLOSE = 'awaiting_close',

    /** Closed. The page returns to what an organization sees before it converts. */
    CLOSED = 'closed',
}
